import { useMemo, useState } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { Search } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AddressInput } from '@/components/data/AddressInput'
import { Button } from '@/components/ui/button'
import { OperationsListBody } from '@/components/operations/OperationsListBody'
import { useOperationsList } from '@/hooks/api/useOperationsList'
import { isHexAddress } from '@/lib/utils'

export const Route = createFileRoute('/players/')({
    component: PlayersIndex,
})

function PlayersIndex() {
    const [value, setValue] = useState('')
    const valid = isHexAddress(value)

    return (
        <div className="space-y-6">
            <div>
                <h1 className="text-2xl font-semibold">Players</h1>
                <p className="text-muted-foreground text-sm">
                    Look up a player's on-chain status and KYC tier across configured chains.
                </p>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Address lookup</CardTitle>
                    <CardDescription>Enter a 0x-prefixed wallet address.</CardDescription>
                </CardHeader>
                <CardContent>
                    <form
                        className="flex flex-wrap gap-2"
                        onSubmit={(e) => {
                            e.preventDefault()
                            // Link below handles navigation; this submit is just for ENTER key handling.
                        }}
                    >
                        <AddressInput
                            value={value}
                            onChange={(e) => setValue(e.target.value.trim())}
                            className="max-w-md flex-1"
                        />
                        <Button asChild disabled={!valid}>
                            <Link
                                to="/players/$address"
                                params={{ address: value }}
                                disabled={!valid}
                                className={!valid ? 'pointer-events-none opacity-50' : undefined}
                            >
                                <Search className="h-4 w-4" /> Look up
                            </Link>
                        </Button>
                    </form>
                </CardContent>
            </Card>

            <RecentOperationsCard />
        </div>
    )
}

function RecentOperationsCard() {
    const query = useOperationsList(25)
    const items = useMemo(() => query.data?.pages.flatMap((p) => p.operations) ?? [], [query.data])

    return (
        <Card>
            <CardHeader>
                <CardTitle>Recent player operations</CardTitle>
                <CardDescription>
                    Latest player registrations processed by the gaming service (GET
                    /v1/operations).
                </CardDescription>
            </CardHeader>
            <CardContent>
                <OperationsListBody query={query} items={items} />
            </CardContent>
        </Card>
    )
}
